import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, Phone, Pencil, Mail, CalendarDays, Users2, Radio, Tag as TagIcon, Fingerprint, History, KanbanSquare, User as UserIcon, Compass, Building2, StickyNote, Trash2, ShieldCheck } from "lucide-react";
import { format, parseISO } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";

import ContactModal from "../ContactModal";
import NumeroComBandeira from "../NumeroComBandeira";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { canalDoTicket } from "../../helpers/canalDoTicket";
import { corDeTextoSobre } from "../../helpers/corDeTexto";
import useLarguraArrastavel from "../../hooks/useLarguraArrastavel";
import usePermissions from "../../hooks/usePermissions";
import useModules from "../../hooks/useModules";
import Atividades from "../Atividades";
import StatusDoContato from "../StatusDoContato";
import NaoContatar from "../NaoContatar";
import HistoricoDeMudancas from "../HistoricoDeMudancas";
import CamposPersonalizados from "../CamposPersonalizados";

const InfoRow = ({ icon: Icon, label, children }) => (
	<div className="flex items-start gap-2.5 text-sm">
		<Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
		<div className="min-w-0">
			<p className="text-xs text-muted-foreground">{label}</p>
			<div className="truncate font-medium">{children}</div>
		</div>
	</div>
);

// O que esta pessoa já pediu, o que está esperando resposta e o que virou
// pedido — em dinheiro, não em contagem de linhas.
//
// Fica na ficha porque é a pergunta que muda o tom da conversa: quem tem um
// orçamento aberto de R$ 4.000 esperando resposta não é atendido como quem
// nunca pediu nada. Antes disso, saber isso exigia sair da conversa e procurar
// o contato em três telas diferentes.
const dinheiro = (valor) =>
	Number(valor || 0).toLocaleString("pt-BR", {
		style: "currency",
		currency: "BRL",
	});

// Os mesmos rótulos que as telas de Orçamentos, Propostas e Pedidos usam —
// copiados de lá de propósito: importar a página inteira só para ler um mapa
// arrastaria a tela toda para dentro do bundle da ficha, que abre em toda
// conversa. Se um rótulo mudar lá, muda aqui.
const ROTULO_DE_STATUS = {
	// orçamento e proposta
	draft: "rascunho",
	sent: "esperando resposta",
	viewed: "visto pelo cliente",
	accepted: "aceito",
	rejected: "recusado",
	expired: "vencido",
	// pedido
	pending_payment: "a pagar",
	paid: "pago",
	separating: "separando",
	shipped: "despachado",
	delivered: "entregue",
	returned: "devolvido",
	cancelled: "cancelado",
};

const LinhaDeVenda = ({ item, valor, aoAbrir }) => (
	<button
		type="button"
		onClick={aoAbrir}
		className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted"
	>
		<span className="min-w-0 flex-1 truncate">
			nº {item.number}
			{item.status && (
				<span className="text-muted-foreground">
					{" "}
					· {ROTULO_DE_STATUS[item.status] || item.status}
				</span>
			)}
		</span>
		<span className="shrink-0 tabular-nums text-xs text-muted-foreground">
			{dinheiro(valor)}
		</span>
	</button>
);

const GrupoDeVendas = ({ titulo, dados, chave, campoValor, aoAbrir, aoVerTodos }) => {
	const itens = dados?.[chave] || [];

	if (!itens.length) return null;

	// A soma é a dos itens à vista, e o rótulo diz isso quando há mais no
	// servidor — somar 3 de 12 e chamar de total seria número inventado.
	const total = itens.reduce((soma, i) => soma + Number(i[campoValor] || 0), 0);
	const quantos = dados?.count ?? itens.length;

	return (
		<div>
			<div className="flex items-baseline justify-between gap-2">
				<button
					type="button"
					onClick={aoVerTodos}
					className="text-xs font-medium text-primary hover:underline"
				>
					{titulo} ({quantos})
				</button>
				<span className="tabular-nums text-xs text-muted-foreground">
					{itens.length < quantos ? "últimos " : ""}
					{dinheiro(total)}
				</span>
			</div>
			<div className="mt-0.5">
				{itens.slice(0, 3).map((item) => (
					<LinhaDeVenda
						key={item.id}
						item={item}
						valor={item[campoValor]}
						aoAbrir={() => aoAbrir(item)}
					/>
				))}
			</div>
		</div>
	);
};

// Quando vai sair, e o quê. Só o que ainda está por vir: agendamento que já
// saiu é histórico da conversa, e ele está logo ali nas mensagens.
const LinhaDeAgendamento = ({ item }) => (
	<div className="flex items-start gap-2 rounded px-2 py-1 text-sm">
		<span className="min-w-0 flex-1 truncate">{item.body || "(sem texto)"}</span>
		<span className="shrink-0 text-xs text-muted-foreground">
			{item.scheduledFor
				? format(parseISO(item.scheduledFor), "dd/MM HH:mm")
				: ""}
		</span>
	</div>
);

// Os mesmos rótulos da aba Auditoria em Relatórios — duas telas contando a
// mesma história não podem usar dois vocabulários.
const ACAO_DA_AUDITORIA = {
	aceitou: "Aceitou",
	devolveu: "Devolveu para Aguardando",
	transferiu: "Transferiu",
	encerrou: "Encerrou",
	reabriu: "Reabriu",
	espiou: "Espiou",
	excluiu: "Excluiu",
};

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const canal = canalDoTicket(ticket);
	// WhatsApp (sessão ou Cloud API) é o único canal cujo identificador É um
	// telefone.
	const ehTelefone = canal.chave.startsWith("whatsapp");
	const [modalOpen, setModalOpen] = useState(false);
	const navigate = useNavigate();

	// Histórico e cartões do contato: as duas perguntas que quem atende faz
	// antes de responder ("já falamos com essa pessoa?", "tem negócio aberto?").
	//
	// Tudo num estado só, carimbado com o contato de quem veio (`de`). Assim a
	// ficha nunca mostra o que é de OUTRO contato: se a consulta do atual
	// falhar, a tela fica vazia — não com as notas do anterior.
	const FICHA_VAZIA = {
		de: null,
		anteriores: [],
		cartoes: [],
		notas: [],
		status: [],
		// Dinheiro: o que esta pessoa já pediu, o que está esperando resposta e
		// o que virou pedido. Quem atende decide o tom da conversa por isso.
		vendas: { orcamentos: null, propostas: null, pedidos: null },
		// Compromissos assumidos com esta pessoa: o que já está marcado para
		// sair e a cadência que está rodando atrás dela.
		agendados: [],
		followUps: [],
		// Rastro do atendimento deste cliente: quem aceitou, devolveu,
		// transferiu, encerrou. Só chega para quem tem "system:audit".
		auditoria: [],
		falhou: false,
	};
	const [fichaCarregada, setFichaCarregada] = useState(FICHA_VAZIA);
	const [notaNova, setNotaNova] = useState("");
	// Pedir de novo. Uma falha na consulta deixava a ficha vazia em silêncio: a
	// observação recém-escrita sumia da tela sem que nada explicasse, e o único
	// jeito de tentar outra vez era fechar e reabrir o painel.
	const [tentativa, setTentativa] = useState(0);
	// Conta as gravações feitas AQUI (anotar, apagar). Resposta de uma consulta
	// que saiu antes de uma delas é mais velha que a tela e não pode desfazê-la.
	const escritasLocais = useRef(0);

	// O que a empresa contratou. Sem isto a ficha prometeria o que o plano não
	// tem: o backend responde 403 e o bloco sumiria sozinho, mas depois de uma
	// requisição inútil por conversa aberta — e um bloco que aparece vazio hoje
	// e some amanhã confunde mais do que ajuda.
	const { temModulo } = useModules();
	const { temPermissao } = usePermissions();
	// Rastro do atendimento é coisa de quem administra — a mesma régua da aba
	// "Auditoria" em Relatórios.
	const podeAuditar = temPermissao("system:audit");
	const comErp = temModulo("erp");
	const comPropostas = temModulo("propostas");
	const comAgendamentos = temModulo("schedules");
	// Os runs são de quem gerencia follow-up (`can` no backend); sem a
	// permissão o pedido só voltava 403.
	const comFollowUp = temModulo("followUp") && temPermissao("followups:manage");
	const comKanban = temModulo("kanban");

	const contactId = contact?.id;

	const ficha = fichaCarregada.de === contactId ? fichaCarregada : FICHA_VAZIA;
	const { anteriores, cartoes, notas, status, vendas, agendados, followUps } = ficha;
	const auditoria = ficha.auditoria || [];

	// Locação é pedido com data de devolução — ver o comentário no bloco.
	const locacoes = (vendas?.pedidos?.orders || []).filter(
		(pedido) => pedido.returnDueAt,
	);

	useEffect(() => {
		if (!open || !contactId) return;

		let valeAinda = true;
		const marca = escritasLocais.current;

		// Cada bloco falha por conta própria: empresa sem o módulo Kanban recebe
		// 403 ali e nem por isso pode perder o histórico de atendimentos.
		//
		// 403 é resposta ESPERADA (módulo não contratado, usuário sem
		// permissão) e segue em silêncio, como antes. Qualquer outra falha —
		// 401 no meio da renovação do token, rede caindo — precisa aparecer:
		// era ela que apagava a observação da tela sem dizer nada.
		const vazio = (err) => ({
			data: null,
			falhou: true,
			esperado: err?.response?.status === 403,
		});

		// Módulo fora do plano: não é falha, é ausência. Não conta como erro na
		// tarja de "não deu para carregar tudo desta ficha".
		const nada = Promise.resolve({ data: null, falhou: false });

		Promise.all([
			// showAll respeita o escopo do usuário no backend: quem só enxerga os
			// próprios atendimentos continua vendo só os próprios.
			api
				.get("/tickets", {
					params: { contactId, showAll: "true", needsCount: "false" },
				})
				.catch(vazio),
			comKanban ? api.get(`/kanban/cards/by-contact/${contactId}`).catch(vazio) : nada,
			api.get(`/contacts/${contactId}/notes`).catch(vazio),
			// Status do contato nas últimas 24h. Falha silenciosa como os
			// vizinhos: não é dado crítico, e um erro aqui não pode custar o
			// histórico de atendimentos ao lado.
			api.get(`/status-recebidos/contato/${contactId}`).catch(vazio),
			// Orçamento, proposta, pedido, agendamento e follow-up DESTE contato.
			// Todas as rotas já filtram por contato — não há endpoint novo aqui,
			// é a lista de sempre recortada.
			//
			// O que o plano não cobre nem é pedido: `nada` resolve na hora, sem
			// requisição e sem bloco. O `vazio` continua cobrindo o resto (403 de
			// permissão, por exemplo, no caso do follow-up).
			comErp ? api.get(`/quotes?contactId=${contactId}`).catch(vazio) : nada,
			comPropostas
				? api.get(`/proposals?contactId=${contactId}`).catch(vazio)
				: nada,
			comErp ? api.get(`/orders?contactId=${contactId}`).catch(vazio) : nada,
			comAgendamentos
				? api.get(`/scheduled-messages/by-contact/${contactId}`).catch(vazio)
				: nada,
			// Só as cadências VIVAS: histórico de follow-up encerrado é assunto
			// da tela de Follow-ups, não da ficha.
			comFollowUp
				? api
						.get(`/follow-ups/runs`, {
							params: { contactId, status: "pending,scheduled,running" },
						})
						.catch(vazio)
				: nada,
			// Auditoria do cliente. `vazio` cobre o 403 de quem não administra —
			// esperado, e por isso sem tarja de falha.
			podeAuditar
				? api
						.get("/audit/tickets", { params: { contactId } })
						.catch(vazio)
				: nada,
		]).then((respostas) => {
			if (!valeAinda) return;

			const [
				tickets,
				kanban,
				anotacoes,
				recados,
				orcamentos,
				propostas,
				pedidos,
				agenda,
				cadencias,
				rastro,
			] = respostas;

			setFichaCarregada((atual) => {
				// Bloco que falhou não apaga o que já estava certo na tela: o
				// aviso acima avisa que a ficha pode estar incompleta, e trocar
				// uma lista boa por vazia é justamente o sumiço que se está
				// consertando. Só vale o que é DESTE contato.
				const doMesmo = atual.de === contactId;
				const manter = (resposta, novo, anterior) =>
					resposta.falhou ? (doMesmo ? anterior : []) : novo;

				return {
					de: contactId,
					anteriores: manter(
						tickets,
						(tickets.data?.tickets || []).slice(0, 5),
						atual.anteriores
					),
					cartoes: manter(kanban, kanban.data || [], atual.cartoes),
					status: manter(recados, recados.data || [], atual.status),
					// Anotou (ou apagou) enquanto isto vinha? Então a lista do
					// servidor não conhece a mudança — deixar ela vencer é o que
					// fazia a observação sumir logo depois de gravada.
					notas: manter(
						anotacoes,
						escritasLocais.current === marca || !doMesmo
							? anotacoes.data || []
							: atual.notas,
						atual.notas
					),
					vendas: {
						orcamentos: manter(
							orcamentos,
							orcamentos.data,
							atual.vendas?.orcamentos
						),
						propostas: manter(propostas, propostas.data, atual.vendas?.propostas),
						pedidos: manter(pedidos, pedidos.data, atual.vendas?.pedidos),
					},
					agendados: manter(
						agenda,
						agenda.data?.scheduledMessages || agenda.data || [],
						atual.agendados
					),
					followUps: manter(
						cadencias,
						cadencias.data?.runs || cadencias.data || [],
						atual.followUps
					),
					auditoria: manter(
						rastro,
						rastro.data?.logs || [],
						atual.auditoria
					),
					falhou: respostas.some((r) => r.falhou && !r.esperado),
				};
			});
		});

		return () => {
			valeAinda = false;
		};
	}, [
		open,
		contactId,
		tentativa,
		comErp,
		comPropostas,
		comAgendamentos,
		comFollowUp,
		comKanban,
		podeAuditar,
	]);

	// A outra divisa da conversa: o que este painel ocupa, a conversa não tem.
	// Só de xl para cima — abaixo disso ele cobre a tela inteira, e largura
	// arrastada ali não significaria nada.
	const {
		ref: refDoPainel,
		largura: larguraDoPainel,
		ativo: larguraDoPainelVale,
		iniciarArrasto: arrastarPainel,
	} = useLarguraArrastavel({
		chave: "larguraPainelContato",
		padrao: 320,
		min: 260,
		max: 520,
		invertido: true,
		aPartirDe: 1280,
	});

	const salvarNota = async () => {
		if (!notaNova.trim()) return;

		try {
			const { data } = await api.post(`/contacts/${contactId}/notes`, {
				body: notaNova,
			});
			escritasLocais.current += 1;
			// A consulta deste contato pode nem ter voltado ainda: nesse caso a
			// ficha guardada é do contato ANTERIOR, e herdar o resto dela seria
			// pendurar histórico alheio no nome de quem está na tela.
			setFichaCarregada((f) => {
				const base = f.de === contactId ? f : { ...FICHA_VAZIA, de: contactId };
				return { ...base, notas: [data, ...base.notas] };
			});
			setNotaNova("");
		} catch (err) {
			toastError(err);
		}
	};

	const apagarNota = async (nota) => {
		try {
			await api.delete(`/contacts/${contactId}/notes/${nota.id}`);
			escritasLocais.current += 1;
			setFichaCarregada((f) =>
				f.de === contactId
					? { ...f, notas: f.notas.filter((n) => n.id !== nota.id) }
					: f
			);
		} catch (err) {
			toastError(err);
		}
	};

	const initials = (contact?.name || "?")
		.split(" ")
		.slice(0, 2)
		.map((n) => n[0])
		.join("")
		.toUpperCase();

	return (
		// Só de xl (1280px) para cima o painel empurra a conversa para o lado:
		// antes disso não há lado. Em 1024 a conta é 264 de menu + 340 de lista
		// + 320 dele = sobravam 100px de conversa, e no celular vazava para
		// fora da tela. Até lá ele vira uma camada por cima — véu atrás, como
		// o painel de vendas — e o X (ou o véu) fecha e devolve a conversa.
		<>
		{open && (
			<div
				aria-hidden="true"
				onClick={handleDrawerClose}
				className="fixed inset-0 z-20 bg-black/40 xl:hidden"
			/>
		)}
		<div
			ref={refDoPainel}
			style={
				open && larguraDoPainelVale ? { width: larguraDoPainel } : undefined
			}
			className={cn(
				"relative h-full overflow-hidden transition-[width] duration-200 ease-in-out",
				// Mesmo comportamento do painel de vendas: camada que desliza da
				// direita abaixo de xl, com sombra.
				// Camada de tela cheia abaixo de xl. `fixed`, e não `absolute`: como
				// `absolute`, o painel fechado não saía da tela com o
				// `translate-x-full` — ficava 126px para dentro, cobrindo a conversa,
				// cortado na borda e inerte (pointer-events: none), sem que nada na
				// tela explicasse aquilo. Preso à viewport, o fechado sai inteiro e o
				// aberto ocupa a tela.
				// Camada de tela cheia abaixo de xl, presa à VIEWPORT.
				//
				// Fechado, ele simplesmente não existe no celular (`max-xl:hidden`),
				// em vez de sair de cena por `translate`. O deslizar era bonito e não
				// funcionava: com `position: absolute` o painel fechado parava 126px
				// dentro da tela, cortado e inerte, e mesmo preso à viewport a classe
				// de aberto não vencia a de fechado — aberto e fechado pintavam no
				// mesmo lugar, fora da tela. Sem transformação não há o que empatar.
				"max-xl:fixed max-xl:inset-y-0 max-xl:right-0 max-xl:z-30 max-xl:w-full max-xl:max-w-[420px] max-xl:shadow-xl",
				open
					? "w-80 shrink-0"
					: "w-0 shrink-0 max-xl:hidden"
			)}
		>
		{open && (
			<div
				role="separator"
				aria-orientation="vertical"
				aria-label="Arraste para mudar a largura do painel"
				title="Arraste para mudar a largura do painel"
				onPointerDown={arrastarPainel}
				className="absolute inset-y-0 left-0 z-10 hidden w-1.5 cursor-col-resize touch-none transition-colors hover:bg-primary/30 active:bg-primary/40 xl:block"
			/>
		)}
		{/* Antes este bloco tinha largura fixa de 320px: com o painel maior,
		    o conteúdo continuava estreito e sobrava faixa vazia ao lado. */}
		<div className="flex h-full w-full flex-col border-l bg-background max-xl:w-full">
			<div className="flex min-h-[57px] shrink-0 items-center gap-2 border-b bg-muted/40 px-2">
				<Button variant="ghost" size="icon" onClick={handleDrawerClose}>
					<X className="h-5 w-5" />
				</Button>
				<span className="text-sm font-medium">{i18n.t("contactDrawer.header")}</span>
			</div>

			{loading ? (
				<ContactDrawerSkeleton />
			) : (
				<div className="flex-1 space-y-3 overflow-y-auto bg-muted/30 p-4">
					{ficha.falhou && (
						<div
							role="status"
							className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
						>
							<p className="flex-1">
								Não deu para carregar tudo desta ficha. O que aparece abaixo
								pode estar incompleto.
							</p>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setTentativa((t) => t + 1)}
							>
								Tentar de novo
							</Button>
						</div>
					)}

					<ContactModal
						open={modalOpen}
						onClose={() => setModalOpen(false)}
						contactId={contact.id}
					/>

					{/* Profile card */}
					<div className="flex flex-col items-center gap-2 rounded-xl border bg-card p-6 text-center">
						<Avatar className="h-28 w-28">
							<AvatarImage src={contact.profilePicUrl} alt={contact.name} />
							<AvatarFallback className="text-2xl">{initials}</AvatarFallback>
						</Avatar>
						<p className="text-base font-semibold">{contact.name}</p>
						{contact.isGroup && (
							<Badge variant="secondary" className="gap-1">
								<Users2 className="h-3 w-3" />
								Grupo
							</Badge>
						)}
						<div className="mt-2 flex flex-wrap justify-center gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setModalOpen(true)}
							>
								<Pencil className="h-3.5 w-3.5" />
								{i18n.t("contactDrawer.buttons.edit")}
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => navigate(`/contacts/${contact.id}`)}
							>
								<History className="h-3.5 w-3.5" />
								Abrir página do contato
							</Button>
						</div>
					</div>

					<NaoContatar key={contact.id} contactId={contact.id} />

					{/* Contact details */}
					<div className="space-y-3 rounded-xl border bg-card p-4">
						{/* Canal não telefônico (Telegram, Messenger, Direct) guarda no
						    mesmo campo o id da pessoa NAQUELE canal, não um telefone.
						    Rotulá-lo "Telefone" com bandeira e máscara inventa um número
						    que não existe — e o link "tel:" ligaria para ele. */}
						{contact.number &&
							(ehTelefone ? (
								<InfoRow icon={Phone} label="Telefone">
									<a href={`tel:${contact.number}`} className="text-primary hover:underline">
										<NumeroComBandeira
											numero={contact.number}
											ehGrupo={contact.isGroup}
										/>
									</a>
								</InfoRow>
							) : (
								<InfoRow icon={Fingerprint} label={`Identificador no ${canal.rotulo}`}>
									<span className="tabular-nums">{contact.number}</span>
								</InfoRow>
							))}
						{contact.email && (
							<InfoRow icon={Mail} label="E-mail">
								<a href={`mailto:${contact.email}`} className="text-primary hover:underline">
									{contact.email}
								</a>
							</InfoRow>
						)}
						{contact.createdAt && (
							<InfoRow icon={CalendarDays} label="Cliente desde">
								{format(parseISO(contact.createdAt), "dd/MM/yyyy")}
							</InfoRow>
						)}
						{contact.user?.name && (
							<InfoRow icon={UserIcon} label="Responsável">
								{contact.user.name}
							</InfoRow>
						)}
						{contact.organization?.name && (
							<InfoRow icon={Building2} label="Empresa">
								{contact.organization.name}
							</InfoRow>
						)}
						{contact.source && (
							<InfoRow icon={Compass} label="Origem">
								{contact.source}
							</InfoRow>
						)}
						{contact.tags?.length > 0 && (
							<div className="space-y-1 pt-1">
								{/* "do contato" no rótulo de propósito: as etiquetas do
								    atendimento aparecem logo abaixo e são outra coisa. */}
								<p className="text-xs text-muted-foreground">
									Etiquetas do contato
								</p>
								<div className="flex flex-wrap gap-1.5">
									{contact.tags.map((tag) => (
										<span
											key={tag.id}
											className="rounded-full px-2 py-0.5 text-[11px] font-medium"
											style={{
												backgroundColor: tag.color,
												color: corDeTextoSobre(tag.color),
											}}
										>
											{tag.name}
										</span>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Current ticket context */}
					{ticket?.id && (
						<div className="space-y-3 rounded-xl border bg-card p-4">
							<p className="text-sm font-semibold">Atendimento atual</p>
							{ticket.queue && (
								<InfoRow icon={TagIcon} label="Departamento">
									<span className="inline-flex items-center gap-1.5">
										<span
											className="h-2 w-2 shrink-0 rounded-full"
											style={{ backgroundColor: ticket.queue.color }}
										/>
										{ticket.queue.name}
									</span>
								</InfoRow>
							)}
							{ticket.whatsapp && (
								<InfoRow icon={Radio} label="Conexão">
									{ticket.whatsapp.name}
								</InfoRow>
							)}
							{/* Vazada: é a mesma regra da linha da lista — cheia é
							    etiqueta da pessoa, vazada é da conversa. */}
							{ticket.tags?.length > 0 && (
								<div className="flex flex-wrap gap-1.5 pt-1">
									{ticket.tags.map((tag) => (
										<span
											key={tag.id}
											className="rounded-full border px-2 py-0.5 text-[11px] font-medium"
											style={{ borderColor: tag.color, color: tag.color }}
										>
											{tag.name}
										</span>
									))}
								</div>
							)}
						</div>
					)}

					<StatusDoContato contact={contact} status={status} />

					<div className="rounded-xl border bg-card p-4">
						<p className="mb-2 flex items-center gap-2 text-sm font-semibold">
							<StickyNote className="h-4 w-4 text-muted-foreground" />
							Notas do cliente
						</p>
						<Textarea
							rows={2}
							value={notaNova}
							onChange={(e) => setNotaNova(e.target.value)}
							placeholder="O que a equipe precisa saber sobre esta pessoa."
						/>
						<Button
							size="sm"
							className="mt-2 w-full"
							disabled={!notaNova.trim()}
							onClick={salvarNota}
						>
							Anotar
						</Button>
						<div className="mt-3 space-y-2">
							{notas.map((nota) => (
								<div key={nota.id} className="rounded-lg border bg-muted/40 p-2.5">
									<div className="flex items-start gap-2">
										<p className="min-w-0 flex-1 whitespace-pre-wrap text-sm">
											{nota.body}
										</p>
										<Button
											variant="ghost"
											size="icon"
											className="h-6 w-6 shrink-0"
											onClick={() => apagarNota(nota)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>
									<p className="pt-1 text-xs text-muted-foreground">
										{nota.user?.name || "Autor removido"} ·{" "}
										{nota.createdAt
											? format(parseISO(nota.createdAt), "dd/MM/yyyy HH:mm")
											: ""}
									</p>
								</div>
							))}
						</div>
					</div>

					{anteriores.filter((t) => t.id !== ticket?.id).length > 0 && (
						<div className="rounded-xl border bg-card p-4">
							<p className="mb-2 text-sm font-semibold">
								Atendimentos anteriores
							</p>
							<div className="space-y-1">
								{anteriores
									.filter((t) => t.id !== ticket?.id)
									.map((anterior) => (
										<button
											key={anterior.id}
											type="button"
											onClick={() => navigate(`/tickets/${anterior.id}`)}
											className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted"
										>
											<span className="min-w-0 flex-1 truncate">
												#{anterior.id} · {anterior.queue?.name || "Sem departamento"}
											</span>
											<span className="shrink-0 text-xs text-muted-foreground">
												{anterior.createdAt
													? format(parseISO(anterior.createdAt), "dd/MM/yyyy")
													: ""}
											</span>
										</button>
									))}
							</div>
						</div>
					)}

					{podeAuditar && (
						<div className="space-y-2 rounded-xl border bg-card p-4">
							<p className="flex items-center gap-1.5 text-sm font-semibold">
								<ShieldCheck className="h-4 w-4 text-muted-foreground" />
								Auditoria
							</p>
							<p className="text-xs text-muted-foreground">
								Quem aceitou, devolveu, transferiu e encerrou os atendimentos
								deste cliente.
							</p>

							{auditoria.length === 0 && (
								<p className="text-sm text-muted-foreground">
									Nada registrado ainda. Aceite, devolução, transferência e
									encerramento deste cliente aparecem aqui.
								</p>
							)}

							<div className="space-y-1">
								{auditoria.slice(0, 8).map((log) => (
									<div
										key={log.id}
										className="flex items-start gap-2 rounded px-2 py-1 text-sm odd:bg-muted/40"
									>
										<span className="min-w-0 flex-1">
											<span className="font-medium">
												{ACAO_DA_AUDITORIA[log.action] || log.action}
											</span>{" "}
											<span className="text-muted-foreground">
												· {log.user?.name ||
													(log.atorSistema
														? `Sistema — ${log.atorSistema}`
														: "sem autor")}
												{log.para?.name ? ` → ${log.para.name}` : ""}
												{" · nº "}
												{log.ticketId}
											</span>
										</span>
										<span className="shrink-0 text-xs text-muted-foreground">
											{log.createdAt
												? format(parseISO(log.createdAt), "dd/MM HH:mm")
												: ""}
										</span>
									</div>
								))}
							</div>

							{auditoria.length > 8 && (
								<button
									type="button"
									onClick={() => navigate("/reports?aba=auditoria")}
									className="text-xs font-medium text-primary hover:underline"
								>
									Ver tudo em Relatórios › Auditoria
								</button>
							)}
						</div>
					)}

					{(vendas?.orcamentos?.quotes?.length ||
						vendas?.propostas?.proposals?.length ||
						vendas?.pedidos?.orders?.length) > 0 && (
						<div className="space-y-3 rounded-xl border bg-card p-4">
							<p className="text-sm font-semibold">Vendas</p>

							<GrupoDeVendas
								titulo="Orçamentos"
								dados={vendas.orcamentos}
								chave="quotes"
								campoValor="total"
								aoAbrir={(item) => navigate(`/vendas/${item.id}`)}
								aoVerTodos={() => navigate(`/vendas?contactId=${contactId}&visao=lista`)}
							/>

							<GrupoDeVendas
								titulo="Propostas"
								dados={vendas.propostas}
								chave="proposals"
								campoValor="totalValue"
								aoAbrir={(item) => navigate(item.quoteId ? `/vendas/${item.quoteId}` : `/propostas/${item.id}`)}
								aoVerTodos={() => navigate(`/propostas?contactId=${contactId}`)}
							/>

							<GrupoDeVendas
								titulo="Pedidos"
								dados={vendas.pedidos}
								chave="orders"
								campoValor="total"
								aoAbrir={(item) => navigate(item.quoteId ? `/vendas/${item.quoteId}` : `/pedidos?pedido=${item.id}`)}
								aoVerTodos={() => navigate(`/vendas?contactId=${contactId}&visao=lista`)}
							/>

							{/* Locação é pedido com data de devolução (`returnDueAt`) — é
							    assim que o backend as separa em /orders/locacoes. Sai da
							    mesma lista de pedidos que já veio: uma requisição a menos,
							    e o que importa aqui não é o valor, é QUANDO tem de voltar,
							    com o atraso em vermelho. */}
							{locacoes.length > 0 && (
								<div>
									<div className="flex items-baseline justify-between gap-2">
										<button
											type="button"
											onClick={() => navigate("/locacoes")}
											className="text-xs font-medium text-primary hover:underline"
										>
											Locações ({locacoes.length})
										</button>
									</div>
									<div className="mt-0.5">
										{locacoes.slice(0, 3).map((item) => {
											const vencida =
												!item.returnedAt &&
												parseISO(item.returnDueAt) < new Date();

											return (
												<button
													key={item.id}
													type="button"
													onClick={() => navigate("/locacoes")}
													className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted"
												>
													<span className="min-w-0 flex-1 truncate">
														nº {item.number}
														<span className="text-muted-foreground">
															{" "}
															· {item.returnedAt ? "devolvida" : "em posse"}
														</span>
													</span>
													<span
														className={cn(
															"shrink-0 text-xs",
															vencida
																? "font-medium text-destructive"
																: "text-muted-foreground",
														)}
													>
														{vencida ? "venceu " : "volta "}
														{format(parseISO(item.returnDueAt), "dd/MM")}
													</span>
												</button>
											);
										})}
									</div>
								</div>
							)}
						</div>
					)}

					{agendados.length > 0 && (
						<div className="rounded-xl border bg-card p-4">
							<p className="mb-2 text-sm font-semibold">
								Agendado ({agendados.length})
							</p>
							<div className="space-y-0.5">
								{agendados.slice(0, 3).map((item) => (
									<LinhaDeAgendamento key={item.id} item={item} />
								))}
							</div>
							<button
								type="button"
								onClick={() => navigate("/scheduled-messages")}
								className="mt-1 px-2 text-xs font-medium text-primary hover:underline"
							>
								Ver agendamentos
							</button>
						</div>
					)}

					{followUps.length > 0 && (
						<div className="rounded-xl border bg-card p-4">
							<p className="mb-2 text-sm font-semibold">
								Follow-up em andamento ({followUps.length})
							</p>
							<div className="space-y-0.5">
								{followUps.slice(0, 3).map((run) => (
									<div
										key={run.id}
										className="flex items-start gap-2 rounded px-2 py-1 text-sm"
									>
										<span className="min-w-0 flex-1 truncate">
											{run.rule?.name || run.followUpRule?.name || "Cadência"}
										</span>
										<span className="shrink-0 text-xs text-muted-foreground">
											{run.nextRunAt
												? format(parseISO(run.nextRunAt), "dd/MM HH:mm")
												: ROTULO_DE_STATUS[run.status] || run.status || ""}
										</span>
									</div>
								))}
							</div>
							<button
								type="button"
								onClick={() => navigate("/follow-ups")}
								className="mt-1 px-2 text-xs font-medium text-primary hover:underline"
							>
								Ver follow-ups
							</button>
						</div>
					)}

					{cartoes.length > 0 && (
						<div className="rounded-xl border bg-card p-4">
							<p className="mb-2 text-sm font-semibold">Kanban</p>
							<div className="space-y-1">
								{cartoes.map((cartao) => (
									<div
										key={cartao.id}
										className="flex items-center gap-2 rounded px-2 py-1 text-sm"
									>
										<span className="min-w-0 flex-1 truncate">
											{cartao.title}
										</span>
										<span className="shrink-0 text-xs text-muted-foreground">
											{cartao.kanbanColumn?.name}
											{cartao.value
												? ` · ${Number(cartao.value).toLocaleString("pt-BR", {
														style: "currency",
														currency: "BRL",
													})}`
												: ""}
										</span>
									</div>
								))}
							</div>
							<Button
								variant="outline"
								size="sm"
								className="mt-2 w-full"
								onClick={() => navigate("/kanban")}
							>
								<KanbanSquare className="h-3.5 w-3.5" />
								Abrir o quadro
							</Button>
						</div>
					)}

					{contact?.id && (
						<div className="rounded-xl border bg-card p-4">
							<CamposPersonalizados entity="contact" entityId={contact.id} />
						</div>
					)}

					{contact?.id && (
						<div className="rounded-xl border bg-card p-4">
							<p className="mb-2 text-sm font-semibold">Próximas atividades</p>
							<Atividades contactId={contact.id} />
						</div>
					)}

					{contact?.id && (
						<div className="rounded-xl border bg-card p-4">
							<p className="mb-2 text-sm font-semibold">Histórico</p>
							<HistoricoDeMudancas entity="contact" entityId={contact.id} />
						</div>
					)}

					{contact?.extraInfo?.length > 0 && (
						<div className="rounded-xl border bg-card p-4">
							<p className="mb-2 text-sm font-semibold">
								{i18n.t("contactDrawer.extraInfo")}
							</p>
							<div className="space-y-2">
								{contact.extraInfo.map((info) => (
									<div key={info.id} className="rounded-lg border bg-muted/40 p-2.5">
										<p className="text-xs font-medium text-muted-foreground">
											{info.name}
										</p>
										<div className="truncate pt-0.5 text-sm">
											<MarkdownWrapper>{info.value}</MarkdownWrapper>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
		</div>
		</>
	);
};

export default ContactDrawer;
